import axios from "axios";
import { contentfulClient } from "./../services/contentful";
import {
  IAppsFields,
  ICategories,
  ICategoriesFields,
  IInstallation,
  IMenuLinksFields,
  ITypes,
  ITypesFields,
} from "./../types/appstore";
import {
  IApps,
  IAppsFields as GeneratedAppFields,
} from "../../types/contentful";
import { fullAPIURL } from "../utils/requests";
import { Setting } from "../types/setting";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";

export type FieldQueryKey = `fields.${keyof GeneratedAppFields}`;
export type ContentfulQuery = Partial<
  Record<FieldQueryKey, string | number | boolean>
>;

const appStoreApi = axios.create({
  baseURL: `${fullAPIURL}/v2/appstore`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

appStoreApi.interceptors.request.use(requestAccessTokenInterceptor);

appStoreApi.interceptors.request.use(refreshAccessTokenInterceptor);

appStoreApi.interceptors.response.use(responseStatusInterceptor);

const clientSetting = axios.create({
  baseURL: `${fullAPIURL}/v2/client`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

clientSetting.interceptors.request.use(requestAccessTokenInterceptor);

clientSetting.interceptors.request.use(refreshAccessTokenInterceptor);

clientSetting.interceptors.response.use(responseStatusInterceptor);

export async function getCategories(): Promise<ICategoriesFields[]> {
  const categoryEntries = (await contentfulClient.getEntries({
    content_type: "categories",
    select: "fields",
  })) as {
    items: ICategories[];
  };

  const categories = categoryEntries.items.map((item) => ({
    id: item.sys.id,
    ...item.fields,
  })) as ICategoriesFields[];

  return categories;
}

export async function getTypes(): Promise<ITypesFields[]> {
  const categoryEntries = (await contentfulClient.getEntries({
    content_type: "types",
    select: "fields",
  })) as {
    items: ITypes[];
  };

  const categories = categoryEntries.items.map((item) => ({
    id: item.sys.id,
    ...item.fields,
  })) as ITypesFields[];

  return categories;
}

function sanitizeApps(apps: IApps[]): IAppsFields[] {
  return apps
    .filter((app) => app.fields.uuid || app.fields.beta)
    .map((item) => ({
      ...item.fields,
      id: item.sys.id,
      categories: item.fields.categories
        ? (item.fields.categories.map((item) => ({
            id: item.sys.id,
            ...item.fields,
          })) as ICategoriesFields[])
        : [],
      types: item.fields.types
        ? (item.fields.types.map((item) => ({
            id: item.sys.id,
            ...item.fields,
          })) as ITypesFields[])
        : [],
      menuLinks: item.fields.menuLinks
        ? (item.fields.menuLinks.map((item) => ({
            id: item.sys.id,
            ...item.fields,
          })) as IMenuLinksFields[])
        : [],
    }));
}

export async function getAppById(slug: string): Promise<IAppsFields> {
  const appEntries = (await contentfulClient.getEntries({
    "fields.slug": slug,
    content_type: "apps",
    select: "fields",
  })) as {
    items: IApps[];
  };

  if (appEntries.items.length === 0) {
    throw new Error("No app found with this slug");
  }

  const apps = sanitizeApps(appEntries.items);

  return apps[0];
}

/**
 * paginated list contentful apps fields
 * filter by field values using the contentful query syntax e.g. field.[fieldNae]: value
 * @returns contentful entries in reverse created order
 */
export function listApps(
  contentFulQueryParams?: ContentfulQuery & { skip?: number; limit?: number },
) {
  return contentfulClient.getEntries<GeneratedAppFields>({
    ...contentFulQueryParams,
    content_type: "apps",
    select: "fields",
    order: "-sys.createdAt",
  });
}

/**
 * @deprecated use listApps with generated contentful types instead
 * @returns
 */
export async function getApps(): Promise<IAppsFields[]> {
  const appEntries = (await contentfulClient.getEntries({
    content_type: "apps",
    select: "fields",
  })) as {
    items: IApps[];
  };

  return sanitizeApps(appEntries.items);
}

export async function getInstallations(clientId: string) {
  const { data } = await appStoreApi.get<IInstallation[]>(
    `${clientId}/install`,
  );
  return data;
}

export async function getAppsSettingsAndInstallations(
  clientId: string,
): Promise<{
  installations: IInstallation[];
  settings: Setting[];
}> {
  const [
    {
      data: { data: settings },
    },
    { data: installations },
  ] = await Promise.all([
    clientSetting.get(`client/${clientId}/setting?limit=200`) as Promise<{
      data: {
        data: Setting[];
      };
    }>,
    appStoreApi.get(`${clientId}/install`) as Promise<{
      data: IInstallation[];
    }>,
  ]);

  return { installations, settings };
}
